import { useState, useRef, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  listManagers,
  syncSlackId,
  deleteUserPermanently,
  getSlackWorkspaceUsers,
  syncAllSlackIds,
  importSlackUsers,
  bulkUpdateUsers,
} from '@/api/users'
import { listDepartments, listDesignations, type Department, type Designation } from '@/api/orgstructure'
import type { User, UserCreate, UserUpdate, ShiftSchedule, DayAbbrev } from '@/types'
import {
  Search,
  MoreVertical,
  UserPlus,
  Edit,
  UserX,
  UserCheck,
  X,
  RefreshCw,
  Trash2,
  Download,
  Check,
  Calendar,
  Loader2,
  Users as UsersIcon,
  ArrowRight,
  LayoutGrid,
  List,
} from 'lucide-react'

type ViewMode = 'grid' | 'list'
const VIEW_MODE_KEY = 'users-view-mode'

// Deterministic avatar tone from the user's name, so the same person always
// gets the same color across renders (purely cosmetic — no data meaning).
const AVATAR_TONES = [
  'bg-violet-100 text-violet-700', 'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700', 'bg-rose-100 text-rose-700',
]
function avatarTone(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_TONES[hash % AVATAR_TONES.length]
}

type ModalMode = 'create' | 'edit' | null

const DAY_ABBREVS: DayAbbrev[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_LABELS: Record<DayAbbrev, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

type ScheduleMap = Record<DayAbbrev, { enabled: boolean; start: string; end: string }>

function buildScheduleFromSimple(standup: string, eod: string, shiftDays: string): ScheduleMap {
  const active = new Set(shiftDays.split(',').map(s => s.trim().toLowerCase()).filter(Boolean))
  const s = (standup || '09:00').slice(0, 5)
  const e = (eod || '17:00').slice(0, 5)
  return DAY_ABBREVS.reduce((acc, d) => {
    acc[d] = { enabled: active.has(d), start: s, end: e }
    return acc
  }, {} as ScheduleMap)
}

function hydrateSchedule(
  schedule: ShiftSchedule | null | undefined,
  standup: string,
  eod: string,
  shiftDays: string,
): ScheduleMap {
  const base = buildScheduleFromSimple(standup, eod, shiftDays)
  if (!schedule) return base
  for (const d of DAY_ABBREVS) {
    const cfg = schedule[d]
    if (cfg) {
      base[d] = {
        enabled: Boolean(cfg.enabled),
        start: (cfg.start || base[d].start).slice(0, 5),
        end: (cfg.end || base[d].end).slice(0, 5),
      }
    }
  }
  return base
}

function scheduleToPayload(sched: ScheduleMap): ShiftSchedule {
  const out: ShiftSchedule = {}
  for (const d of DAY_ABBREVS) {
    const cfg = sched[d]
    out[d] = { enabled: cfg.enabled, start: cfg.start, end: cfg.end }
  }
  return out
}

function validateSchedule(sched: ScheduleMap): string | null {
  const anyEnabled = DAY_ABBREVS.some(d => sched[d].enabled)
  if (!anyEnabled) return 'At least one working day must be enabled.'
  for (const d of DAY_ABBREVS) {
    const cfg = sched[d]
    if (!cfg.enabled) continue
    const [sh, sm] = cfg.start.split(':').map(Number)
    const [eh, em] = cfg.end.split(':').map(Number)
    // Allow overnight shifts (end < start). Only reject identical start/end.
    if (eh * 60 + em === sh * 60 + sm) {
      return `${DAY_LABELS[d]}: start and end time must differ.`
    }
  }
  return null
}

function deriveSimpleFromSchedule(sched: ScheduleMap): {
  standup_time: string
  eod_time: string
  shift_days: string
} {
  const enabled = DAY_ABBREVS.filter(d => sched[d].enabled)
  if (enabled.length === 0) {
    return { standup_time: '09:00', eod_time: '17:00', shift_days: 'mon,tue,wed,thu,fri' }
  }
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
  }
  let earliestStart = Infinity
  let latestEnd = -Infinity
  let earliestStartStr = '09:00'
  let latestEndStr = '17:00'
  for (const d of enabled) {
    const sMin = toMin(sched[d].start)
    const eMin = toMin(sched[d].end)
    if (sMin < earliestStart) { earliestStart = sMin; earliestStartStr = sched[d].start }
    if (eMin > latestEnd) { latestEnd = eMin; latestEndStr = sched[d].end }
  }
  return {
    standup_time: earliestStartStr,
    eod_time: latestEndStr,
    shift_days: enabled.join(','),
  }
}

interface PerDayScheduleEditorProps {
  schedule: ScheduleMap
  onChange: (next: ScheduleMap) => void
  compact?: boolean
}

function PerDayScheduleEditor({ schedule, onChange, compact }: PerDayScheduleEditorProps) {
  const updateDay = (day: DayAbbrev, patch: Partial<ScheduleMap[DayAbbrev]>) => {
    onChange({ ...schedule, [day]: { ...schedule[day], ...patch } })
  }

  // Use the first enabled day as the "source" for Apply Throughout buttons
  const sourceDay = DAY_ABBREVS.find(d => schedule[d].enabled) ?? 'mon'
  const sourceCfg = schedule[sourceDay]

  const applyStartThroughout = () => {
    const next = { ...schedule }
    for (const d of DAY_ABBREVS) {
      if (next[d].enabled) next[d] = { ...next[d], start: sourceCfg.start }
    }
    onChange(next)
  }
  const applyEndThroughout = () => {
    const next = { ...schedule }
    for (const d of DAY_ABBREVS) {
      if (next[d].enabled) next[d] = { ...next[d], end: sourceCfg.end }
    }
    onChange(next)
  }

  return (
    <div className={`space-y-2 ${compact ? '' : 'rounded-lg border border-gray-200 p-3 bg-gray-50/50'}`}>
      {/* Apply Throughout buttons */}
      <div className="flex gap-2 pb-2 border-b border-gray-200">
        <button
          type="button"
          onClick={applyStartThroughout}
          className="flex-1 px-2 py-1.5 text-xs font-medium text-brand bg-brand/10 border border-brand/30 rounded hover:bg-brand/20"
          title={`Apply ${sourceCfg.start} as opening time to all enabled days`}
        >
          Apply Opening Hours Throughout
        </button>
        <button
          type="button"
          onClick={applyEndThroughout}
          className="flex-1 px-2 py-1.5 text-xs font-medium text-brand bg-brand/10 border border-brand/30 rounded hover:bg-brand/20"
          title={`Apply ${sourceCfg.end} as closing time to all enabled days`}
        >
          Apply Closing Hours Throughout
        </button>
      </div>

      {/* Per-day rows */}
      {DAY_ABBREVS.map(d => {
        const cfg = schedule[d]
        return (
          <div key={d} className="flex items-center gap-2">
            <label className="flex items-center gap-2 w-24 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => updateDay(d, { enabled: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
              />
              <span className={`text-sm font-medium ${cfg.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                {DAY_LABELS[d]}
              </span>
            </label>
            {cfg.enabled ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="time"
                  value={cfg.start}
                  onChange={(e) => updateDay(d, { start: e.target.value })}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <span className="text-xs text-gray-500">to</span>
                <input
                  type="time"
                  value={cfg.end}
                  onChange={(e) => updateDay(d, { end: e.target.value })}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
            ) : (
              <div className="flex-1 text-sm italic text-gray-400">Off</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface DropdownPosition {
  top?: number
  bottom?: number
  right: number
}

interface UserActionsDropdownProps {
  user: User
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
  onEdit: () => void
  onEditQuotas: () => void
  onSyncSlack: () => void
  onDeactivate: () => void
  onReactivate: () => void
  onDelete: () => void
  isSyncing: boolean
}

function UserActionsDropdown({
  user,
  isOpen,
  onToggle,
  onClose,
  onEdit,
  onEditQuotas,
  onSyncSlack,
  onDeactivate,
  onReactivate,
  onDelete,
  isSyncing
}: UserActionsDropdownProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<DropdownPosition>({ right: 0 })

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const dropdownHeight = 200
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setPosition({
          bottom: viewportHeight - rect.top + 4,
          right: window.innerWidth - rect.right
        })
      } else {
        setPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right
        })
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onToggle}
        className="p-2 hover:bg-gray-100 rounded-lg"
      >
        <MoreVertical className="h-4 w-4 text-gray-500" />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          style={{
            top: position.top !== undefined ? `${position.top}px` : undefined,
            bottom: position.bottom !== undefined ? `${position.bottom}px` : undefined,
            right: `${position.right}px`
          }}
        >
          <button
            onClick={onEdit}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Edit className="h-4 w-4" />
            Edit User
          </button>
          <button
            onClick={onEditQuotas}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Calendar className="h-4 w-4" />
            Leave Quotas
          </button>
          <button
            onClick={onSyncSlack}
            disabled={isSyncing}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-brand hover:bg-brand/10 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : user.slack_id ? 'Re-sync Slack' : 'Sync Slack'}
          </button>
          {user.is_active ? (
            <button
              onClick={onDeactivate}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
            >
              <UserX className="h-4 w-4" />
              Deactivate
            </button>
          ) : (
            <button
              onClick={onReactivate}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50"
            >
              <UserCheck className="h-4 w-4" />
              Reactivate
            </button>
          )}
          <button
            onClick={onDelete}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete Permanently
          </button>
        </div>
      )}
    </>
  )
}

export default function Users() {
  const queryClient = useQueryClient()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) === 'list' ? 'list' : 'grid'
    } catch {
      return 'grid'
    }
  })
  const changeViewMode = (mode: ViewMode) => {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      /* storage unavailable — preference just won't persist */
    }
  }
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined)
  const [modalMode, setModalMode] = useState<ModalMode>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [quotaUser, setQuotaUser] = useState<User | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [showBulkRole, setShowBulkRole] = useState(false)
  const [showBulkManager, setShowBulkManager] = useState(false)
  const [showBulkPassword, setShowBulkPassword] = useState(false)
  const [bulkPassword, setBulkPassword] = useState('')
  const [showBulkSchedule, setShowBulkSchedule] = useState(false)
  const [bulkSchedule, setBulkSchedule] = useState<ScheduleMap>(() =>
    buildScheduleFromSimple('09:00', '17:00', 'mon,tue,wed,thu,fri')
  )
  const [formSchedule, setFormSchedule] = useState<ScheduleMap>(() =>
    buildScheduleFromSimple('09:00', '17:00', 'mon,tue,wed,thu,fri')
  )
  const [showShiftEditor, setShowShiftEditor] = useState(false)
  const [showSlackModal, setShowSlackModal] = useState(false)
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<Set<string>>(new Set())
  const [importRole, setImportRole] = useState('team_member')
  const [useSlackTimezone, setUseSlackTimezone] = useState(true)
  const [timezoneOverride, setTimezoneOverride] = useState('Asia/Karachi')

  // Form state
  const [formData, setFormData] = useState<UserCreate>({
    email: '',
    name: '',
    role: 'team_member',
    timezone: 'Asia/Karachi',
    standup_time: '09:00',
    eod_time: '17:00',
    shift_days: 'mon,tue,wed,thu,fri',
  })
  const [formError, setFormError] = useState<string | null>(null)

  // Queries
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', search, filterActive],
    queryFn: () => listUsers({
      search: search || undefined,
      is_active: filterActive,
      per_page: 100
    }),
  })

  const { data: managers } = useQuery({
    queryKey: ['managers'],
    queryFn: listManagers,
  })

  // Department/designation names for the card grid's meta row — same lists
  // UserDetail.tsx already fetches; resolving the id here is display-only.
  const { data: departments } = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const { data: designations } = useQuery({ queryKey: ['designations'], queryFn: () => listDesignations() })
  const departmentName = (id: string | null | undefined) => departments?.find((d: Department) => d.id === id)?.name
  const designationName = (id: string | null | undefined) => designations?.find((d: Designation) => d.id === id)?.title

  const { data: slackUsers, isLoading: isLoadingSlack, refetch: refetchSlackUsers } = useQuery({
    queryKey: ['slack-workspace-users'],
    queryFn: getSlackWorkspaceUsers,
    enabled: showSlackModal,
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      closeModal()
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.detail || 'Failed to create user')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdate }) =>
      updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      closeModal()
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.detail || 'Failed to update user')
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: reactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const syncSlackMutation = useMutation({
    mutationFn: syncSlackId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setOpenDropdown(null)
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to sync Slack ID')
      setOpenDropdown(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteUserPermanently,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to delete user')
    },
  })

  const syncAllMutation = useMutation({
    mutationFn: syncAllSlackIds,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['slack-workspace-users'] })
      const parts = [`Synced ${result.synced_count} user(s)`, `${result.already_synced_count} already synced`]
      if (result.deactivated_count > 0) parts.push(`${result.deactivated_count} deactivated (removed from Slack)`)
      if (result.not_found_count > 0) parts.push(`${result.not_found_count} not found in Slack`)
      alert(parts.join('. ') + '.')
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to sync all users')
    },
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: bulkUpdateUsers,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setSelectedUsers(new Set())
      setShowBulkRole(false)
      setShowBulkManager(false)
      alert(`Updated ${result.updated_count} user(s)`)
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to bulk update users')
    },
  })

  const importMutation = useMutation({
    mutationFn: importSlackUsers,
    onSuccess: (users) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['slack-workspace-users'] })
      setSelectedSlackUsers(new Set())
      if (users.length > 0) {
        alert(`Successfully imported ${users.length} user(s)`)
      }
      setShowSlackModal(false)
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to import users')
    },
  })

  const openCreateModal = () => {
    setFormData({
      email: '',
      name: '',
      role: 'team_member',
      timezone: 'Asia/Karachi',
    })
    setFormSchedule(buildScheduleFromSimple('09:00', '17:00', 'mon,tue,wed,thu,fri'))
    setFormError(null)
    setSelectedUser(null)
    setModalMode('create')
  }

  const openEditModal = (user: User) => {
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      timezone: user.timezone,
      manager_id: user.manager_id,
    })
    setFormSchedule(hydrateSchedule(
      user.shift_schedule,
      user.standup_time || '09:00',
      user.eod_time || '17:00',
      user.shift_days || 'mon,tue,wed,thu,fri',
    ))
    setFormError(null)
    setSelectedUser(user)
    setModalMode('edit')
    setOpenDropdown(null)
  }

  const closeModal = () => {
    setModalMode(null)
    setSelectedUser(null)
    setFormError(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    // Validate schedule
    const err = validateSchedule(formSchedule)
    if (err) {
      setFormError(err)
      return
    }

    const schedulePayload = scheduleToPayload(formSchedule)
    const simple = deriveSimpleFromSchedule(formSchedule)

    if (modalMode === 'create') {
      createMutation.mutate({
        ...formData,
        manager_id: formData.manager_id || undefined,
        standup_time: simple.standup_time,
        eod_time: simple.eod_time,
        shift_days: simple.shift_days,
        shift_schedule: schedulePayload,
      })
    } else if (modalMode === 'edit' && selectedUser) {
      updateMutation.mutate({
        userId: selectedUser.id,
        data: {
          name: formData.name,
          role: formData.role,
          timezone: formData.timezone,
          manager_id: formData.manager_id || undefined,
          standup_time: simple.standup_time,
          eod_time: simple.eod_time,
          shift_days: simple.shift_days,
          shift_schedule: schedulePayload,
          ...(formData.password ? { password: formData.password } : {}),
        },
      })
    }
  }

  const handleDeactivate = (userId: string) => {
    if (confirm('Are you sure you want to deactivate this user?')) {
      deactivateMutation.mutate(userId)
    }
    setOpenDropdown(null)
  }

  const handleReactivate = (userId: string) => {
    reactivateMutation.mutate(userId)
    setOpenDropdown(null)
  }

  const handleDelete = (userId: string, userName: string) => {
    if (confirm(`Are you sure you want to permanently delete "${userName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(userId)
    }
    setOpenDropdown(null)
  }

  const handleSlackImport = () => {
    if (selectedSlackUsers.size === 0) {
      alert('Please select at least one user to import')
      return
    }
    importMutation.mutate({
      slack_ids: Array.from(selectedSlackUsers),
      role: importRole,
      timezone_override: useSlackTimezone ? undefined : timezoneOverride,
    })
  }

  const toggleSlackUser = (slackId: string) => {
    const newSelected = new Set(selectedSlackUsers)
    if (newSelected.has(slackId)) {
      newSelected.delete(slackId)
    } else {
      newSelected.add(slackId)
    }
    setSelectedSlackUsers(newSelected)
  }

  const selectAllUnimported = () => {
    if (!slackUsers) return
    const unimported = slackUsers.filter(u => !u.is_imported).map(u => u.slack_id)
    setSelectedSlackUsers(new Set(unimported))
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700'
      case 'manager':
        return 'bg-blue-100 text-blue-700'
      case 'project_lead':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const formatRole = (role: string) => {
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  const toggleUser = (userId: string) => {
    const next = new Set(selectedUsers)
    if (next.has(userId)) next.delete(userId)
    else next.add(userId)
    setSelectedUsers(next)
  }

  const selectableUsers = usersData?.items ?? []
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((u) => selectedUsers.has(u.id))
  const selectedUsersList = usersData?.items.filter((u) => selectedUsers.has(u.id)) ?? []
  const hasActiveSelected = selectedUsersList.some((u) => u.is_active)
  const hasInactiveSelected = selectedUsersList.some((u) => !u.is_active)

  const toggleAll = () => {
    if (allSelected) {
      setSelectedUsers(new Set())
    } else {
      setSelectedUsers(new Set(selectableUsers.map((u) => u.id)))
    }
  }

  const unimportedCount = slackUsers?.filter(u => !u.is_imported).length || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 text-sm">Manage team members in your organization</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              setShowSlackModal(true)
              refetchSlackUsers()
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Import from Slack</span>
            <span className="sm:hidden">Import</span>
          </button>
          <button
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${syncAllMutation.isPending ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncAllMutation.isPending ? 'Syncing...' : 'Sync All'}</span>
            <span className="sm:hidden">{syncAllMutation.isPending ? '...' : 'Sync'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
          />
        </div>
        <select
          value={filterActive === undefined ? 'all' : filterActive ? 'active' : 'inactive'}
          onChange={(e) => {
            const val = e.target.value
            setFilterActive(val === 'all' ? undefined : val === 'active')
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
        >
          <option value="all">All Users</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
        <div className="inline-flex shrink-0 self-end sm:self-auto rounded-lg border border-gray-300 bg-white p-0.5" role="group" aria-label="View mode">
          {([['grid', LayoutGrid, 'Grid view'], ['list', List, 'List view']] as const).map(([mode, Icon, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => changeViewMode(mode)}
              title={label}
              aria-label={label}
              aria-pressed={viewMode === mode}
              className={`rounded-md p-2 transition-colors ${
                viewMode === mode ? 'bg-brand text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedUsers.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 bg-brand/10 border border-brand/30 rounded-lg">
          <span className="text-sm font-medium text-brand-dark">
            {selectedUsers.size} selected
          </span>
          <div className="h-4 w-px bg-brand/40" />

          {/* Bulk Role */}
          <div className="relative">
            <button
              onClick={() => { setShowBulkRole(!showBulkRole); setShowBulkManager(false); setShowBulkPassword(false) }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Change Role
            </button>
            {showBulkRole && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {(['team_member', 'admin'] as const).map((role) => (
                  <button
                    key={role}
                    onClick={() => bulkUpdateMutation.mutate({ user_ids: Array.from(selectedUsers), updates: { role } })}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {formatRole(role)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bulk Manager */}
          <div className="relative">
            <button
              onClick={() => { setShowBulkManager(!showBulkManager); setShowBulkRole(false); setShowBulkPassword(false) }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Assign Manager
            </button>
            {showBulkManager && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-60 overflow-y-auto">
                <button
                  onClick={() => bulkUpdateMutation.mutate({ user_ids: Array.from(selectedUsers), updates: { manager_id: '' } })}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
                >
                  No manager
                </button>
                {managers?.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => bulkUpdateMutation.mutate({ user_ids: Array.from(selectedUsers), updates: { manager_id: m.id } })}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {m.name} ({formatRole(m.role)})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bulk Shift Schedule (opens centered modal) */}
          <button
            onClick={() => {
              // Pre-fill from the first selected user's current schedule so admins
              // see the existing values instead of defaults.
              const firstUser = selectedUsersList[0]
              if (firstUser) {
                setBulkSchedule(hydrateSchedule(
                  firstUser.shift_schedule,
                  firstUser.standup_time || '09:00',
                  firstUser.eod_time || '17:00',
                  firstUser.shift_days || 'mon,tue,wed,thu,fri',
                ))
              } else {
                setBulkSchedule(buildScheduleFromSimple('09:00', '17:00', 'mon,tue,wed,thu,fri'))
              }
              setShowBulkSchedule(true)
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Change Shift Schedule
          </button>

          {/* Bulk Password */}
          <div className="relative">
            <button
              onClick={() => { setShowBulkPassword(!showBulkPassword); setShowBulkRole(false); setShowBulkManager(false) }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Set Password
            </button>
            {showBulkPassword && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50 space-y-3">
                <input
                  type="text"
                  value={bulkPassword}
                  onChange={(e) => setBulkPassword(e.target.value)}
                  placeholder="New password"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={() => {
                    if (!bulkPassword.trim()) return
                    bulkUpdateMutation.mutate({
                      user_ids: Array.from(selectedUsers),
                      updates: { password: bulkPassword },
                    })
                    setShowBulkPassword(false)
                    setBulkPassword('')
                  }}
                  disabled={!bulkPassword.trim()}
                  className="w-full px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          <div className="h-4 w-px bg-brand/40" />
          {hasInactiveSelected && (
            <button
              onClick={() => {
                if (confirm(`Activate ${selectedUsersList.filter(u => !u.is_active).length} user(s)?`)) {
                  bulkUpdateMutation.mutate({ user_ids: selectedUsersList.filter(u => !u.is_active).map(u => u.id), updates: { is_active: true } })
                }
              }}
              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-lg hover:bg-green-50"
            >
              Activate
            </button>
          )}
          {hasActiveSelected && (
            <button
              onClick={() => {
                if (confirm(`Deactivate ${selectedUsersList.filter(u => u.is_active).length} user(s)?`)) {
                  bulkUpdateMutation.mutate({ user_ids: selectedUsersList.filter(u => u.is_active).map(u => u.id), updates: { is_active: false } })
                }
              }}
              className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-white border border-orange-300 rounded-lg hover:bg-orange-50"
            >
              Deactivate
            </button>
          )}
          <button
            onClick={() => setSelectedUsers(new Set())}
            className="ml-auto px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      )}

      {/* Select-all — the grid's per-card checkboxes drive individual selection */}
      {selectableUsers.length > 0 && viewMode === 'grid' && (
        <label className="flex items-center gap-2 text-sm text-gray-500">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
          />
          Select all {selectableUsers.length} shown
        </label>
      )}

      {/* Users grid */}
      {isLoading ? (
        <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-lg border border-gray-200">Loading...</div>
      ) : usersData?.items.length === 0 ? (
        <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-lg border border-gray-200">No users found</div>
      ) : viewMode === 'list' ? (
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all users"
                    className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                  />
                </th>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Job Title</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Shift</th>
                <th className="px-4 py-3">Status</th>
                <th className="w-12 px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {usersData?.items.map((user) => (
                <tr
                  key={user.id}
                  className={!user.is_active ? 'bg-gray-50' : selectedUsers.has(user.id) ? 'bg-brand/5' : 'hover:bg-gray-50'}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => toggleUser(user.id)}
                      aria-label={`Select ${user.name}`}
                      className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-[14rem]">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt={user.name} className="h-9 w-9 shrink-0 rounded-full" />
                      ) : (
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarTone(user.name)}`}>
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0">
                        <Link
                          to={`/${orgSlug}/admin/users/${user.employee_id || user.id}`}
                          className="block truncate font-medium text-gray-900 hover:text-[#EC5B2C] hover:underline"
                        >
                          {user.name}
                        </Link>
                        <span className="block truncate text-xs text-gray-500">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{user.employee_id || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{departmentName(user.department_id) || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{designationName(user.designation_id) || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {formatRole(user.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                    {user.shift_schedule && Object.keys(user.shift_schedule).length > 0 && (
                      <span className="mr-1 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700">Custom</span>
                    )}
                    {(user.standup_time || '09:00').slice(0, 5)}–{(user.eod_time || '17:00').slice(0, 5)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Inactive</span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-right">
                    <UserActionsDropdown
                      user={user}
                      isOpen={openDropdown === user.id}
                      onToggle={() => setOpenDropdown(openDropdown === user.id ? null : user.id)}
                      onClose={() => setOpenDropdown(null)}
                      onEdit={() => openEditModal(user)}
                      onEditQuotas={() => { setOpenDropdown(null); setQuotaUser(user) }}
                      onSyncSlack={() => syncSlackMutation.mutate(user.id)}
                      onDeactivate={() => handleDeactivate(user.id)}
                      onReactivate={() => handleReactivate(user.id)}
                      onDelete={() => handleDelete(user.id, user.name)}
                      isSyncing={syncSlackMutation.isPending}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {usersData?.items.map((user) => (
            <div
              key={user.id}
              className={`relative rounded-xl border p-4 shadow-sm transition-colors ${
                !user.is_active ? 'bg-gray-50 border-gray-200' : selectedUsers.has(user.id) ? 'bg-brand/5 border-brand/30' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <input
                  type="checkbox"
                  checked={selectedUsers.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                  className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                />
                <UserActionsDropdown
                  user={user}
                  isOpen={openDropdown === user.id}
                  onToggle={() => setOpenDropdown(openDropdown === user.id ? null : user.id)}
                  onClose={() => setOpenDropdown(null)}
                  onEdit={() => openEditModal(user)}
                  onEditQuotas={() => { setOpenDropdown(null); setQuotaUser(user) }}
                  onSyncSlack={() => syncSlackMutation.mutate(user.id)}
                  onDeactivate={() => handleDeactivate(user.id)}
                  onReactivate={() => handleReactivate(user.id)}
                  onDelete={() => handleDelete(user.id, user.name)}
                  isSyncing={syncSlackMutation.isPending}
                />
              </div>

              <div className="flex items-center gap-3 mt-1">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="h-12 w-12 shrink-0 rounded-full" />
                ) : (
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold ${avatarTone(user.name)}`}>
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <Link
                    to={`/${orgSlug}/admin/users/${user.employee_id || user.id}`}
                    className="block truncate font-medium text-gray-900 hover:text-[#EC5B2C] hover:underline"
                  >
                    {user.name}
                  </Link>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {user.employee_id && (
                      <span className="rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold px-1.5 py-0.5">{user.employee_id}</span>
                    )}
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {formatRole(user.role)}
                    </span>
                  </div>
                </div>
              </div>

              {(departmentName(user.department_id) || designationName(user.designation_id)) && (
                <p className="text-xs text-gray-500 truncate mt-3">
                  {[departmentName(user.department_id), designationName(user.designation_id)].filter(Boolean).join(' · ')}
                </p>
              )}

              <div className="text-xs text-gray-400 mt-1">
                {user.shift_schedule && Object.keys(user.shift_schedule).length > 0 ? (
                  <>
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700 mr-1">Custom</span>
                    {DAY_ABBREVS
                      .filter(d => user.shift_schedule?.[d]?.enabled)
                      .map(d => `${DAY_LABELS[d]} ${(user.shift_schedule![d]!.start).slice(0,5)}–${(user.shift_schedule![d]!.end).slice(0,5)}`)
                      .join(' · ') || '—'}
                  </>
                ) : (
                  <>
                    {(user.standup_time || '09:00').slice(0, 5)}–{(user.eod_time || '17:00').slice(0, 5)} ·{' '}
                    {(user.shift_days || 'mon,tue,wed,thu,fri')
                      .split(',')
                      .map(d => d.charAt(0).toUpperCase() + d.slice(1))
                      .join(' · ')}
                  </>
                )}
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                {user.is_active ? (
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>
                ) : (
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Inactive</span>
                )}
                <Link
                  to={`/${orgSlug}/admin/users/${user.employee_id || user.id}`}
                  title="View full details"
                  className="rounded-full p-2 text-gray-400 hover:text-brand hover:bg-brand/10 transition-colors"
                >
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Add New User' : 'Edit User'}
                </h2>
                <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Password field (admin only) */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password {modalMode === 'create' ? '*' : ''}
                                  </label>
                                  <input
                                    type="password"
                                    value={formData.password || ''}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                                    placeholder={modalMode === 'create' ? 'Set initial password' : 'Leave blank to keep unchanged'}
                                  />
                                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={modalMode === 'edit'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand disabled:bg-gray-100 disabled:text-gray-500"
                    placeholder="user@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  >
                    <option value="team_member">Team Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manager
                  </label>
                  <select
                    value={formData.manager_id || ''}
                    onChange={(e) => setFormData({ ...formData, manager_id: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  >
                    <option value="">No manager</option>
                    {managers?.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({formatRole(m.role)})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shift Schedule
                  </label>
                  <div className="flex items-center justify-between gap-3 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="text-sm text-gray-700 min-w-0 flex-1">
                      {(() => {
                        const enabled = DAY_ABBREVS.filter(d => formSchedule[d].enabled)
                        if (enabled.length === 0) {
                          return <span className="text-gray-400 italic">No working days</span>
                        }
                        // Check if all enabled days share identical start/end times
                        const first = formSchedule[enabled[0]]
                        const uniform = enabled.every(d =>
                          formSchedule[d].start === first.start && formSchedule[d].end === first.end
                        )
                        if (uniform) {
                          return (
                            <span>
                              <span className="font-medium">{enabled.map(d => DAY_LABELS[d]).join(', ')}</span>
                              <span className="text-gray-500"> · {first.start}–{first.end}</span>
                            </span>
                          )
                        }
                        return (
                          <div className="space-y-0.5">
                            {enabled.map(d => (
                              <div key={d} className="text-xs">
                                <span className="font-medium text-gray-700">{DAY_LABELS[d]}</span>
                                <span className="text-gray-500"> · {formSchedule[d].start}–{formSchedule[d].end}</span>
                              </div>
                            ))}
                          </div>
                        )
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowShiftEditor(true)}
                      className="shrink-0 px-3 py-1 text-xs font-medium text-brand bg-white border border-brand/30 rounded hover:bg-brand/10"
                    >
                      Edit Schedule
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Asia/Karachi">Karachi (PKT)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : modalMode === 'create'
                      ? 'Add User'
                      : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Individual Shift Editor Modal (nested over form) */}
      {showShiftEditor && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowShiftEditor(false)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Edit Shift Schedule</h2>
                <button onClick={() => setShowShiftEditor(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <PerDayScheduleEditor schedule={formSchedule} onChange={setFormSchedule} />
              <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowShiftEditor(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const err = validateSchedule(formSchedule)
                    if (err) { setFormError(err); return }
                    setFormError(null)
                    setShowShiftEditor(false)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Shift Schedule Modal */}
      {showBulkSchedule && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowBulkSchedule(false)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Change Shift Schedule</h2>
                  <p className="text-sm text-gray-500">
                    Apply to {selectedUsers.size} selected user{selectedUsers.size === 1 ? '' : 's'}
                    {selectedUsersList[0] && (
                      <> · Pre-filled from <span className="font-medium">{selectedUsersList[0].name}</span></>
                    )}
                  </p>
                </div>
                <button onClick={() => setShowBulkSchedule(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              {selectedUsersList.length > 1 && (() => {
                // Detect if selected users have differing schedules
                const firstJson = JSON.stringify(hydrateSchedule(
                  selectedUsersList[0].shift_schedule,
                  selectedUsersList[0].standup_time || '09:00',
                  selectedUsersList[0].eod_time || '17:00',
                  selectedUsersList[0].shift_days || 'mon,tue,wed,thu,fri',
                ))
                const allSame = selectedUsersList.every(u =>
                  JSON.stringify(hydrateSchedule(
                    u.shift_schedule,
                    u.standup_time || '09:00',
                    u.eod_time || '17:00',
                    u.shift_days || 'mon,tue,wed,thu,fri',
                  )) === firstJson
                )
                return !allSame ? (
                  <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    ⚠ The selected users have different current schedules. Applying here will overwrite them all.
                  </div>
                ) : null
              })()}
              <PerDayScheduleEditor schedule={bulkSchedule} onChange={setBulkSchedule} />
              <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowBulkSchedule(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const err = validateSchedule(bulkSchedule)
                    if (err) { alert(err); return }
                    const simple = deriveSimpleFromSchedule(bulkSchedule)
                    bulkUpdateMutation.mutate({
                      user_ids: Array.from(selectedUsers),
                      updates: {
                        shift_schedule: scheduleToPayload(bulkSchedule),
                        standup_time: simple.standup_time,
                        eod_time: simple.eod_time,
                        shift_days: simple.shift_days,
                      },
                    })
                    setShowBulkSchedule(false)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark"
                >
                  Apply to {selectedUsers.size}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slack Import Modal */}
      {showSlackModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSlackModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Import from Slack</h2>
                  <p className="text-sm text-gray-500">
                    Select users from your Slack workspace to import
                  </p>
                </div>
                <button onClick={() => setShowSlackModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {isLoadingSlack ? (
                <div className="flex-1 flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading Slack users...</span>
                </div>
              ) : !slackUsers || slackUsers.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-gray-500">
                  <UsersIcon className="h-12 w-12 mb-2 text-gray-300" />
                  <p>No Slack users found</p>
                  <p className="text-sm">Make sure your Slack app has the required permissions</p>
                </div>
              ) : (
                <>
                  {/* Controls */}
                  <div className="mb-4 pb-4 border-b space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={selectAllUnimported}
                          className="text-sm text-brand hover:text-brand-dark"
                        >
                          Select all unimported ({unimportedCount})
                        </button>
                        {selectedSlackUsers.size > 0 && (
                          <button
                            onClick={() => setSelectedSlackUsers(new Set())}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Clear selection
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Role:</label>
                        <select
                          value={importRole}
                          onChange={(e) => setImportRole(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand focus:border-brand"
                        >
                          <option value="team_member">Team Member</option>
                          <option value="project_lead">Project Lead</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="timezone"
                            checked={useSlackTimezone}
                            onChange={() => setUseSlackTimezone(true)}
                            className="w-4 h-4 text-brand"
                          />
                          <span className="text-sm text-gray-700">Use Slack timezone</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="timezone"
                            checked={!useSlackTimezone}
                            onChange={() => setUseSlackTimezone(false)}
                            className="w-4 h-4 text-brand"
                          />
                          <span className="text-sm text-gray-700">Override timezone</span>
                        </label>
                      </div>
                      {!useSlackTimezone && (
                        <select
                          value={timezoneOverride}
                          onChange={(e) => setTimezoneOverride(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand focus:border-brand"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="Europe/London">London (GMT)</option>
                          <option value="Europe/Paris">Paris (CET)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                          <option value="Asia/Karachi">Karachi (PKT)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* User List */}
                  <div className="flex-1 overflow-y-auto -mx-6 px-6">
                    <div className="space-y-2">
                      {slackUsers.map((slackUser) => (
                        <div
                          key={slackUser.slack_id}
                          className={`flex items-center p-3 rounded-lg border ${
                            slackUser.is_imported
                              ? 'bg-gray-50 border-gray-200'
                              : selectedSlackUsers.has(slackUser.slack_id)
                              ? 'bg-brand/10 border-brand/40'
                              : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!slackUser.is_imported) {
                              toggleSlackUser(slackUser.slack_id)
                            }
                          }}
                        >
                          <div className="flex-shrink-0 mr-3">
                            {slackUser.is_imported ? (
                              <div className="h-5 w-5 rounded bg-green-100 flex items-center justify-center">
                                <Check className="h-3 w-3 text-green-600" />
                              </div>
                            ) : (
                              <div
                                className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                                  selectedSlackUsers.has(slackUser.slack_id)
                                    ? 'bg-brand border-brand'
                                    : 'border-gray-300'
                                }`}
                              >
                                {selectedSlackUsers.has(slackUser.slack_id) && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                            {slackUser.avatar_url ? (
                              <img
                                src={slackUser.avatar_url}
                                alt={slackUser.name}
                                className="h-10 w-10 rounded-full"
                              />
                            ) : (
                              <span className="text-sm font-medium text-gray-600">
                                {slackUser.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">{slackUser.name}</div>
                            <div className="text-sm text-gray-500 truncate">{slackUser.email}</div>
                            {slackUser.timezone && !slackUser.is_imported && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {useSlackTimezone ? slackUser.timezone : timezoneOverride}
                              </div>
                            )}
                          </div>
                          {slackUser.is_imported && (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                              Already imported
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between items-center mt-4 pt-4 border-t">
                    <span className="text-sm text-gray-500">
                      {selectedSlackUsers.size} user(s) selected
                    </span>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowSlackModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSlackImport}
                        disabled={selectedSlackUsers.size === 0 || importMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50"
                      >
                        {importMutation.isPending ? 'Importing...' : `Import ${selectedSlackUsers.size} User(s)`}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {quotaUser && (
        <UserQuotaModal user={quotaUser} onClose={() => setQuotaUser(null)} />
      )}
    </div>
  )
}

/* ─── User Quota Modal ─── */
function UserQuotaModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient()
  const year = new Date().getFullYear()

  const { data, isLoading } = useQuery({
    queryKey: ['user-quotas', user.id, year],
    queryFn: async () => {
      const api = await import('@/api/leaves')
      return api.getUserQuotas(user.id, year)
    },
  })
  const { data: leaveTypes = [] } = useQuery({
    queryKey: ['leave-types'],
    queryFn: async () => (await import('@/api/leaves')).getLeaveTypes(),
  })

  const [form, setForm] = useState<Record<string, number>>({})
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    if (data?.quotas) {
      const f: Record<string, number> = {}
      for (const [k, v] of Object.entries(data.quotas)) f[k] = (v as any).total
      setForm(f)
    }
  }, [data])

  const saveMut = useMutation({
    mutationFn: async () => {
      const api = await import('@/api/leaves')
      await Promise.all(Object.entries(form).map(([t, days]) =>
        api.setUserQuota({ user_id: user.id, year, type: t, total_days: days })
      ))
    },
    onSuccess: () => {
      setMsg('Saved.')
      qc.invalidateQueries({ queryKey: ['user-quotas', user.id] })
      qc.invalidateQueries({ queryKey: ['leaves'] })
    },
    onError: () => setMsg('Failed to save'),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-semibold">Leave Quotas</h3>
            <p className="text-xs text-gray-500">{user.name} — {year}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {isLoading && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}
          {!isLoading && (
            <>
              <p className="text-xs text-gray-500">
                Leaves with overrides will be used instead of the organization's default quota.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {leaveTypes.map((lt) => {
                  const t = lt.key
                  const usedDays = data?.quotas?.[t]?.used ?? 0
                  const isOverride = data?.overrides && t in data.overrides
                  return (
                    <div key={t}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {lt.label}
                        {isOverride && <span className="ml-1 text-[10px] text-amber-600 font-normal">(override)</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={form[t] ?? 0}
                        onChange={(e) => { setForm({ ...form, [t]: parseFloat(e.target.value) || 0 }); setMsg(null) }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Used: {usedDays} days</p>
                    </div>
                  )
                })}
              </div>
              {msg && <div className="text-sm text-gray-700">{msg}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Close</button>
                <button
                  onClick={() => { setMsg(null); saveMut.mutate() }}
                  disabled={saveMut.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#EC5B2C] hover:bg-[#EA4913] rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
